
import type { PropType } from 'vue';

import axios from 'axios';

import {
  defineComponent,
  ref,
  computed,
  onMounted,
} from 'vue';

import PrimeVueDropdown from 'primevue/dropdown';

import PrimeVueMultiSelect from 'primevue/multiselect';

import PrimeVueListBox from 'primevue/listbox';

import PrimeVueInputText from 'primevue/inputtext';

import PrimeVueButton from 'primevue/button';

import PrimeVueSkeleton from 'primevue/skeleton';

import useFolderActions from '@/composable/useFolderActions';

import type {
  ErrorsMap,
  Folder,
} from '@/types';

/**
 * Выбор папок.
 *
 * Не обёртка над FieldDropdownNext: инлайн-создание живёт в слоте #footer выпадающего списка,
 * а FieldDropdownNext слоты не форвардит.
 */
export default defineComponent({
  components: {
    PrimeVueDropdown,
    PrimeVueMultiSelect,
    PrimeVueListBox,
    PrimeVueInputText,
    PrimeVueButton,
    PrimeVueSkeleton,
  },
  props: {
    modelValue: {
      type: [Number, Array] as PropType<number | number[] | null>,
      default: null,
    },
    name: {
      type: String,
    },
    errorsMap: {
      type: Object as PropType<ErrorsMap>,
      default: () => ({}),
    },
    label: {
      type: String,
    },
    folderableType: {
      type: String,
      default: 'campaign',
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    showClear: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
    /**
     * Всегда раскрытый список вместо выпадающего.
     *
     * Нужен там, где выпадающий список перекрывал бы соседние элементы — например, кнопку
     * сохранения в узкой панели.
     */
    inline: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'update:modelValue',
    'created',
  ],
  setup(props, { emit }) {
    const folderActions = useFolderActions();

    const options = ref<Folder[]>([]);

    const loading = ref(true);

    const newFolderName = ref('');

    const creating = ref(false);

    const createError = ref('');

    const componentName = computed((): string => {
      if (props.inline) {
        return 'PrimeVueListBox';
      }

      return props.multiple ? 'PrimeVueMultiSelect' : 'PrimeVueDropdown';
    });

    const selectedIds = computed((): number[] => {
      if (Array.isArray(props.modelValue)) {
        return props.modelValue;
      }

      return props.modelValue ? [props.modelValue] : [];
    });

    const sortedOptions = computed((): Folder[] => [...options.value].sort((a, b) => {
      const aSelected = Number(selectedIds.value.includes(a.id));

      const bSelected = Number(selectedIds.value.includes(b.id));

      return bSelected - aSelected || a.name.localeCompare(b.name);
    }));

    const fetchFolders = async (): Promise<void> => {
      loading.value = true;

      try {
        const response = await folderActions.fetchFolderOptions(props.folderableType);

        options.value = response.data.data;
      } catch (err) {
        console.error(err);
      }

      loading.value = false;
    };

    const createFolder = async (): Promise<void> => {
      if (!newFolderName.value || creating.value) {
        return;
      }

      creating.value = true;

      createError.value = '';

      try {
        const response = await folderActions.createFolder(props.folderableType, newFolderName.value);

        const folder = response.data;

        options.value = [...options.value, folder];

        emit('update:modelValue', props.multiple ? [...selectedIds.value, folder.id] : folder.id);

        // Другие пикеры на странице о новой папке не знают — пусть родитель их обновит.
        emit('created', folder);

        newFolderName.value = '';
      } catch (err) {
        createError.value = axios.isAxiosError(err) && err.response?.status === 422
          ? Object.values(err.response.data.errors ?? {}).flat().join(' ')
          : String(err);
      }

      creating.value = false;
    };

    onMounted(() => fetchFolders());

    return {
      sortedOptions,
      loading,
      componentName,
      newFolderName,
      creating,
      createError,
      createFolder,
    };
  },
});
